import React, { useEffect, useMemo } from "react";
import {
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  GSEmptyList,
  GSItemList,
  GSSidePanelPage,
} from "golfstatus_react_components";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useMatch } from "react-router-dom";

//import scss
import "./round-hole-assignments.scss";
import { getRouting } from "../../../../helpers/Converters";
import { getRoundName } from "../../../../hooks/roundHooks";
import {
  eventBaseRoute,
  getProp,
  ROUND_ID,
  TOURNAMENT_ID,
} from "../../../../routes/event";
import { useDispatch, useSelector } from "react-redux";
import {
  getRoundHoleAssignments,
  selectHoleAssignments,
  selectLoading,
  selectTournamentRound,
} from "../../../../reducers/tournamentSlice";
import TeamLineItem from "../teams/team-line-item";
import { useTheme } from "../../../../hooks/themeHooks";
import { getDate, getTime } from "../../../../helpers/Dates";
import PageHeader from "../../../page-header";
import { useGolfstatusNavigate } from "../../../../hooks/navigationHooks";

//Name the component
const RoundHoleAssignments = () => {
  const dispatch = useDispatch();

  const [, theme, getStyle] = useTheme();

  const match = useMatch(
    getRouting(eventBaseRoute, [
      TOURNAMENT_ID,
      "rounds",
      ROUND_ID,
      "hole-assignments",
    ]),
  );
  const roundID = getProp(match?.params, ROUND_ID);

  const publicURL = getProp(match?.params, TOURNAMENT_ID);

  const fallback = getRouting(eventBaseRoute, [publicURL, "rounds", roundID]);
  const navigate = useGolfstatusNavigate(fallback);

  const holeAssignments = useSelector(selectHoleAssignments);
  const currentRound = useSelector(selectTournamentRound);
  const loading = useSelector(selectLoading);

  useEffect(() => {
    if (currentRound?.id) {
      dispatch(getRoundHoleAssignments(currentRound?.id));
    }
  }, [dispatch, currentRound?.id]);

  const leftNavigation = () => {
    navigate(-1);
  };

  const getNavigation = () => {
    //Add Title to your component
    return {
      title: `${getRoundName(currentRound)} Hole Assignments`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="No Hole Assignments Yet"
        detail="Please check back closer to the event date for starting hole assignments."
        actions={[
          {
            title: `${getRoundName(currentRound)} Details`,
            type: "grey",
            onClick: () => {
              navigate(-1);
            },
          },
        ]}
      />
    );
  };

  const teamsWithAssignments = useMemo(() => {
    let assignedTeams = [];
    holeAssignments
      ?.forEach?.((ha) => {
        ha.roundHoleAssignmentTournamentTeams?.forEach((rhatt) => {
          assignedTeams.push({
            ...rhatt,
            holeAssignment: ha,
          });
        });
      })
      ?.filter?.((twa) => {
        return !!twa.holeAssignment;
      })
      ?.sort?.((a, b) => {
        const holeSort = a?.holeAssignment?.hole - b?.holeAssignment?.hole;
        const posSort = a?.holeAssignment?.position?.localeCompare(
          b?.holeAssignment?.position,
        );
        const subPosSort = a?.holeAssignment?.subPosition?.localeCompare(
          b?.holeAssignment?.subPosition,
        );
        return holeSort || posSort || subPosSort;
      });

    return assignedTeams;
  }, [holeAssignments]);

  const getHoleAssignment = (team) => {
    const { holeAssignment } = team;
    const courseName = holeAssignment?.roundCourse?.course?.name;
    return (
      <div style={{ ...defaultLayouts.vStack }}>
        <div>
          <div style={{ ...defaultTypography.body }}>
            Hole {holeAssignment?.hole} {holeAssignment?.position} -{" "}
            {getTime(holeAssignment?.teeTime)}
          </div>
        </div>
        {courseName ? (
          <div style={{ ...defaultTypography.bodyRegular.withOpacity() }}>
            {courseName}
          </div>
        ) : null}
        <div style={{ ...defaultTypography.bodyRegular.withOpacity() }}>
          {getDate(holeAssignment?.teeTime)}
        </div>
      </div>
    );
  };

  const getContent = () => {
    return (
      <gs-form>
        <PageHeader style={{ alignItems: "flex-start" }} header={`${getRoundName(currentRound)} Hole Assignments`} />
        <GSItemList
          type="vertical medium-large-gap"
          style={{
            ...defaultLayouts.fullWidth,
            ...defaultPadding.xLargePad.apply("horizontal"),
            ...defaultPadding.smallLayoutPad.apply("vertical"),
          }}
          items={teamsWithAssignments}
          listItem={(team) => (
            <div
              style={{
                ...defaultPadding.xLargePad,
                ...getStyle(theme.surfaceVariant),
                ...defaultLayouts.fullWidth,
                ...defaultBorders.mediumLargeBorderRadius,
                ...defaultGapping.largeGap,
                ...defaultLayouts.vStack,
              }}
            >
              {getHoleAssignment(team)}
              <TeamLineItem
                style={{
                  ...defaultLayouts.fullWidth,
                }}
                {...team.tournamentTeam}
              />
            </div>
          )}
          emptyMessage={getEmptyListMessage()}
          loading={
            loading.includes?.("getTournamentRound") ||
            loading.includes?.("getRoundHoleAssignments")
          }
          loadingMainText="Loading Hole Assignments..."
          loadingSubText="please wait."
        />
      </gs-form>
    );
  };

  return (
    //name the component tag
    <round-hole-assignments>
      <GSSidePanelPage
        header={getNavigation()}
        content={getContent()}
      ></GSSidePanelPage>
    </round-hole-assignments>
  );
};

//export the new namet
export default RoundHoleAssignments;
